import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import type {
  AssetResponse,
  HydratedImage,
} from "../../generated/api/types.gen";
import { saveImageAsAsset } from "../../lib/asset-create";
import { AssetNameField } from "../AssetNameField";
import { ModalPrimaryButton } from "../Button";
import { ModalShell } from "../ModalShell";

interface SaveAsAssetModalProps {
  isOpen: boolean;
  /** The refinement being saved; its bytes are copied server-side. */
  image: HydratedImage;
  onClose: () => void;
}

export function SaveAsAssetModal({
  isOpen,
  image,
  onClose,
}: SaveAsAssetModalProps) {
  const [name, setName] = useState("");
  const queryClient = useQueryClient();
  // Saving the image is not undone when filing the asset into the project
  // fails, so a retry re-files that asset rather than saving a second copy.
  const created = useRef<AssetResponse | null>(null);

  const save = useMutation({
    mutationFn: () =>
      saveImageAsAsset(queryClient, {
        imageId: image.id,
        name: name.trim(),
        projectId: image.project_id,
        created,
      }),
    onSuccess: onClose,
  });

  // The design's subject is the obvious first name for the asset; every open
  // starts from it again so a previous attempt doesn't leak into the next one.
  // The mutation resets with it, or a failed attempt's error would still be
  // showing the next time the modal opens.
  const resetSave = save.reset;
  useEffect(() => {
    if (!isOpen) return;
    setName(image.design_subject ?? "");
    created.current = null;
    resetSave();
  }, [isOpen, image.design_subject, resetSave]);

  if (!isOpen) return null;

  const isDisabled = save.isPending || name.trim().length === 0;
  const handleSave = () => {
    if (!isDisabled) save.mutate();
  };

  return (
    <ModalShell
      onClose={onClose}
      ariaContext="save as asset"
      labelledBy="save-as-asset-title"
    >
      <h2
        id="save-as-asset-title"
        className="pr-10 text-lg font-bold tracking-tight text-white"
      >
        Save as Asset
      </h2>

      <img
        src={image.url ?? ""}
        alt={image.design_subject ?? "Design"}
        className="mx-auto mt-5 aspect-square w-60 rounded-lg object-cover"
      />

      <div className="mt-5 flex items-center gap-2">
        <AssetNameField
          label="Asset Name"
          value={name}
          onChange={setName}
          onSubmit={handleSave}
        />
        <ModalPrimaryButton onClick={handleSave} disabled={isDisabled}>
          {save.isPending ? "Adding…" : "Add"}
        </ModalPrimaryButton>
      </div>

      {save.isError && (
        <p className="mt-4 text-sm text-red-400" role="alert">
          Couldn&apos;t save this asset. Please try again.
        </p>
      )}
    </ModalShell>
  );
}
